/**
 * Navigation Configuration - Single source of truth
 *
 * Centralizes route definitions, icons, labels, groups, and descriptions
 * for the sidebar drawer, dashboard tools grid, and any other navigation surface.
 *
 * Architecture:
 * - NavDestination: core route definition (path, label, icon, group, description)
 * - Groups control visual hierarchy in sidebar (separators between groups)
 * - Dashboard tools grid consumes the same config with color overrides
 * - Active-state logic lives here for consistency
 */

import {
  Home, Leaf, BarChart3, Flag, Images, FileText,
  Stethoscope, MessageCircle, User, GraduationCap, HeartHandshake,
  type LucideIcon,
} from '@/lib/icons';

// ─── Route groups for visual hierarchy ───────────────────────
export type NavGroup = 'primary' | 'daily' | 'library' | 'account';

// ─── Core navigation destination ─────────────────────────────
export interface NavDestination {
  /** Route path (must match React Router) */
  path: string;
  /** Display label (sidebar + tools grid) */
  label: string;
  /** Lucide icon component */
  icon: LucideIcon;
  /** Short helper text shown in sidebar */
  description: string;
  /** Visual group for sidebar hierarchy */
  group: NavGroup;
  /** ARIA label override (defaults to label) */
  ariaLabel?: string;
}

// ─── All navigation destinations ─────────────────────────────
// Order here = order in sidebar. Groups create visual separators.
export const NAV_DESTINATIONS: readonly NavDestination[] = [
  // Primary
  { path: '/today',       label: 'Dashboard',    icon: Home,           description: 'Your home screen',       group: 'primary' },

  // Daily flows
  { path: '/mindfulness', label: 'Mindfulness',  icon: Leaf,           description: 'Daily check-in',         group: 'daily' },
  { path: '/learn',       label: 'Learn',        icon: GraduationCap,  description: 'Guides & articles',      group: 'daily' },
  { path: '/chat',        label: 'Chat',         icon: MessageCircle,  description: 'Ask anything',           group: 'daily' },

  // Library / tools
  { path: '/insights',    label: 'Insights',     icon: BarChart3,      description: 'Patterns & trends',      group: 'library' },
  { path: '/milestones',  label: 'Milestones',   icon: Flag,           description: 'Track development',      group: 'library' },
  { path: '/photos',      label: 'Photos',       icon: Images,         description: 'Baby album',             group: 'library' },
  { path: '/documents',   label: 'Documents',    icon: FileText,       description: 'Medical records',        group: 'library' },
  { path: '/doctor-qa',   label: 'Doctor Q&A',   icon: Stethoscope,    description: 'Questions for visits',   group: 'library' },
  { path: '/partner',     label: 'Partner',      icon: HeartHandshake, description: 'Relationship tools',     group: 'library' },

  // Account
  { path: '/me',          label: 'Me',           icon: User,           description: 'Profile & settings',     group: 'account' },
] as const;

// ─── Dashboard tools grid config ─────────────────────────────
// Maps destination paths to their dashboard tile color token
export const DASHBOARD_TOOL_COLORS: Record<string, string> = {
  '/mindfulness': 'var(--pp-category-mindfulness)',
  '/learn':       'var(--pp-category-learn)',
  '/milestones':  'var(--pp-category-milestones)',
  '/insights':    'var(--pp-category-insights)',
  '/photos':      'var(--pp-category-photos)',
  '/documents':   'var(--pp-category-documents)',
  '/doctor-qa':   'var(--pp-category-doctor)',
  '/partner':     'var(--pp-category-lovemaps)',
};

// Dashboard tools grid uses a subset of destinations + labels may differ
// (e.g., "Documents" → "Docs", "Doctor Q&A" → "Doctor")
export const DASHBOARD_LABEL_OVERRIDES: Record<string, string> = {
  '/mindfulness': 'Reset',
  '/documents':   'Docs',
  '/doctor-qa':   'Doctor',
};

// Paths to show in the dashboard tools grid (order matters)
export const DASHBOARD_TOOL_PATHS = [
  '/mindfulness', '/learn', '/milestones', '/insights',
  '/photos', '/documents', '/doctor-qa', '/partner',
] as const;

// ─── Helpers ─────────────────────────────────────────────────

/** Check if a pathname matches a destination (exact or prefix) */
export function isActiveRoute(pathname: string, destPath: string): boolean {
  return pathname === destPath || pathname.startsWith(destPath + '/');
}

/** Group destinations for sidebar rendering */
export function groupDestinations(): { group: NavGroup; items: NavDestination[] }[] {
  const groups: { group: NavGroup; items: NavDestination[] }[] = [];
  let currentGroup: NavGroup | null = null;

  for (const dest of NAV_DESTINATIONS) {
    if (dest.group !== currentGroup) {
      groups.push({ group: dest.group, items: [dest] });
      currentGroup = dest.group;
    } else {
      groups[groups.length - 1].items.push(dest);
    }
  }

  return groups;
}

/** Get group label for accessibility (optional section headers) */
export const GROUP_LABELS: Record<NavGroup, string> = {
  primary: 'Home',
  daily: 'Daily',
  library: 'Library',
  account: 'Account',
};
